// contactEmail.js — the chat window's "Email" form, as a contact form (v2.07, 2026-10-29).
//
// ⛔ WHAT THIS REPLACES. The six ChatUI*.jsx senders POSTed `to`, `toName`, `from`, `fromName`, `body`
// and `htmlBody` to botapi's unauthenticated /send-email, so anyone could send any mail as our
// mailbox. botapi B1b now picks the recipient and sender itself, from the bot's BotSettings, and
// answers 422 to ANY other property. So this module builds exactly the six fields below, plain text
// only — botapi escapes everything and builds the HTML. Contract: botapi
// RELEASE_NOTES_B1b_2026-10-29.md.
//
// ⚠️ PLAIN JS ON PURPOSE, like guestGate.js: shared by six .jsx files and unit-tested as-is.

/** botapi's length caps (B1b). Over a cap is a 422, so we trim or stop before sending. */
export const CONTACT_EMAIL_LIMITS = Object.freeze({
  name: 200,
  email: 254,
  subject: 200,
  message: 5000,
  transcript: 50000,
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const str = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v)).trim();

/**
 * The agent id to send: the first `agent_…` candidate, else the first non-empty one (botapi also
 * resolves a legacy `asst_…` id). Callers pass what the window already has, most canonical first.
 */
export function pickAgentId(...candidates) {
  const ids = candidates.map(str).filter(Boolean);
  return ids.find((id) => id.startsWith('agent_')) || ids[0] || '';
}

/** The transcript trimmed from the START, so the latest turns survive. */
export function trimTranscript(text, max = CONTACT_EMAIL_LIMITS.transcript) {
  const t = str(text);
  if (t.length <= max) return t;
  const marker = '…\n';
  return marker + t.slice(t.length - (max - marker.length));
}

/**
 * Build the /send-email body, or say why it can't be sent.
 * @returns {{ok:true, payload:object} | {ok:false, error:string}}  `error` is the alert text.
 */
export function buildContactEmail({ agentId, visitorName, visitorEmail, subject, message, transcript }) {
  const L = CONTACT_EMAIL_LIMITS;
  const id = str(agentId);
  const name = str(visitorName).slice(0, L.name);
  const email = str(visitorEmail);
  const subj = str(subject).replace(/[\r\n]+/g, ' ').slice(0, L.subject);
  const msg = str(message);
  const tr = trimTranscript(transcript);

  if (!id) return { ok: false, error: "Email isn't set up for this assistant" };
  if (!name) return { ok: false, error: 'Please enter your name.' };
  if (!email || email.length > L.email || !EMAIL_RE.test(email)) {
    return { ok: false, error: 'Please enter a valid email address (e.g., user@example.com).' };
  }
  if (!subj) return { ok: false, error: 'Please enter a subject.' };
  if (!msg) return { ok: false, error: 'Please write a message before sending.' };
  if (msg.length > L.message) {
    return { ok: false, error: `Your message is too long. Please keep it under ${L.message.toLocaleString('en-US')} characters.` };
  }

  const payload = { agentId: id, visitorName: name, visitorEmail: email, subject: subj, message: msg };
  if (tr) payload.transcript = tr;
  return { ok: true, payload };
}

/**
 * The alert text for a failed /send-email status, or null to use the caller's existing failure
 * alert. botapi hides 5xx message text, so this branches on the status code only.
 */
export function contactEmailFailureText(status) {
  switch (status) {
    case 429: return 'Too many messages, please try again later';
    case 422: return "Email isn't set up for this assistant";
    case 503: return "Email isn't available right now";
    default: return null;
  }
}
